import { createContext, useContext, useState, useEffect } from 'react';

// Import all translation files
import en from '../locales/en.json';
import fr from '../locales/fr.json';
import ar from '../locales/ar.json';
import es from '../locales/es.json';
import pt from '../locales/pt.json';
import nl from '../locales/nl.json';
import tr from '../locales/tr.json';
import hi from '../locales/hi.json';

// All available translations
const translations = { en, fr, ar, es, pt, nl, tr, hi };

// Language configuration
export const LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr' },
  { code: 'fr', name: 'French', nativeName: 'Français', dir: 'ltr' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', dir: 'rtl' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', dir: 'ltr' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', dir: 'ltr' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', dir: 'ltr' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', dir: 'ltr' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', dir: 'ltr' }
];

// Country to language mapping
const COUNTRY_LANGUAGE_MAP = {
  // English speaking countries
  'US': 'en', 'GB': 'en', 'UK': 'en', 'LR': 'en', 'NG': 'en', 'GH': 'en', 
  'KE': 'en', 'AU': 'en', 'NZ': 'en', 'CA': 'en', 'ZA': 'en', 'IE': 'en',
  'SL': 'en', 'ZW': 'en', 'BW': 'en', 'UG': 'en', 'TZ': 'en', 'ZM': 'en',
  
  // French speaking countries
  'FR': 'fr', 'SN': 'fr', 'CI': 'fr', 'ML': 'fr', 'BF': 'fr', 'NE': 'fr',
  'GN': 'fr', 'BE': 'fr', 'CH': 'fr', 'CM': 'fr', 'CD': 'fr', 'MG': 'fr',
  'TG': 'fr', 'BJ': 'fr', 'GA': 'fr', 'CG': 'fr', 'TD': 'fr', 'CF': 'fr',
  'DJ': 'fr', 'KM': 'fr', 'RW': 'fr', 'BI': 'fr', 'HT': 'fr', 'MC': 'fr',
  'LU': 'fr', 'MA': 'fr', 'TN': 'fr', 'DZ': 'fr',
  
  // Arabic speaking countries
  'SA': 'ar', 'AE': 'ar', 'EG': 'ar', 'IQ': 'ar', 'JO': 'ar', 'KW': 'ar',
  'LB': 'ar', 'LY': 'ar', 'OM': 'ar', 'QA': 'ar', 'SD': 'ar', 'SY': 'ar',
  'YE': 'ar', 'BH': 'ar', 'PS': 'ar', 'MR': 'ar', 'SO': 'ar',
  
  // Spanish speaking countries
  'ES': 'es', 'MX': 'es', 'AR': 'es', 'CO': 'es', 'PE': 'es', 'CL': 'es',
  'EC': 'es', 'GT': 'es', 'CU': 'es', 'BO': 'es', 'DO': 'es', 'HN': 'es',
  'PY': 'es', 'SV': 'es', 'NI': 'es', 'CR': 'es', 'PA': 'es', 'UY': 'es',
  'VE': 'es', 'PR': 'es', 'GQ': 'es',
  
  // Portuguese speaking countries
  'PT': 'pt', 'BR': 'pt', 'AO': 'pt', 'MZ': 'pt', 'GW': 'pt', 'CV': 'pt',
  'ST': 'pt', 'TL': 'pt',
  
  // Dutch speaking countries
  'NL': 'nl', 'SR': 'nl', 'AW': 'nl', 'CW': 'nl', 'SX': 'nl',
  
  // Turkish speaking countries
  'TR': 'tr', 'CY': 'tr',
  
  // Hindi speaking countries
  'IN': 'hi'
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState('en');
  const [isLoading, setIsLoading] = useState(true);
  const [detectedCountry, setDetectedCountry] = useState(null);

  // Detect language based on location
  useEffect(() => {
    const detectLanguage = async () => {
      try {
        // Check if user has saved preference
        const savedLanguage = localStorage.getItem('ledgeramt_language');
        if (savedLanguage && translations[savedLanguage]) {
          setLanguage(savedLanguage);
          setIsLoading(false);
          return;
        }

        // Try to detect from IP geolocation
        const response = await fetch('http://ip-api.com/json/?fields=countryCode');
        if (response.ok) {
          const data = await response.json();
          const countryCode = data.countryCode;
          setDetectedCountry(countryCode);
          
          // Map country to language
          const detectedLang = COUNTRY_LANGUAGE_MAP[countryCode] || 'en';
          setLanguage(detectedLang);
          
          // Don't save auto-detected language, let user explicitly choose
        }
      } catch (error) {
        console.log('Could not detect location, using default language');
      } finally {
        setIsLoading(false);
      }
    };

    detectLanguage();
  }, []);

  // Update document direction for RTL languages
  useEffect(() => {
    const langConfig = LANGUAGES.find(l => l.code === language);
    if (langConfig) {
      document.documentElement.dir = langConfig.dir;
      document.documentElement.lang = language;
    }
  }, [language]);

  // Change language and save preference
  const changeLanguage = (langCode) => {
    if (translations[langCode]) {
      setLanguage(langCode);
      localStorage.setItem('ledgeramt_language', langCode);
    }
  };

  // Translation function
  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    // Fallback to English if translation not found
    if (value === undefined) {
      value = translations['en'];
      for (const k of keys) {
        value = value?.[k];
      }
    }
    
    // Return key if still not found
    if (value === undefined) {
      return key;
    }
    
    // Replace parameters
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        value = value.replace(new RegExp(`{{${paramKey}}}`, 'g'), paramValue);
      });
    }
    
    return value;
  };

  // Get current language config
  const getCurrentLanguage = () => {
    return LANGUAGES.find(l => l.code === language) || LANGUAGES[0];
  };

  // Check if current language is RTL
  const isRTL = () => {
    return getCurrentLanguage().dir === 'rtl';
  };

  return (
    <LanguageContext.Provider value={{
      language,
      setLanguage: changeLanguage,
      t,
      translations: translations[language],
      languages: LANGUAGES,
      getCurrentLanguage,
      isRTL,
      detectedCountry,
      isLoading
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageContext;
